import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, Store, MapPin, Mail, Phone, ExternalLink, UserRound, Warehouse, Route as RouteIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useCatalogs } from "@/hooks/useCatalogs";
import { Input } from "@/components/ui/input";

export function StoresDirectory() {
  const [search, setSearch] = useState("");
  const { routes, locations } = useCatalogs();
  const { data, isLoading } = useQuery({
    queryKey: ["stores", search],
    queryFn: async () => (await api.get("/recon/stores", { params: search ? { search } : {} })).data,
    keepPreviousData: true,
  });
  const stores = data?.items || [];

  return (
    <div className="space-y-4" data-testid="stores-directory">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Store size={18} /> Directorio de Tiendas
          </h2>
          <p className="text-sm text-slate-500 mt-1">Tiendas, dueños y datos de contacto de Canales Furniture.</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="stores-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Tienda, dueño, corp, ciudad…" className="h-9 rounded-sm border-slate-300 bg-white text-sm pl-8 w-[260px]" />
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin inline" size={20} /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {stores.map((s) => (
            <div key={s.id} data-testid={`store-card-${s.id}`}
              className="bg-white border border-[#E2E8F0] rounded-sm p-4 hover:border-[#2563EB] transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-heading font-bold tracking-tight text-slate-900">{s.branch}</div>
                  <div className="text-xs text-slate-400 font-mono mt-0.5">{s.corp}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wide font-semibold bg-slate-50 text-slate-500 border border-slate-200 rounded-sm px-1.5 py-0.5">
                  {s.state}
                </span>
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-slate-700"><UserRound size={14} className="text-[#2563EB] shrink-0" /> {s.owner || "—"}</div>
                <div className="flex items-center gap-2 text-slate-500"><MapPin size={14} className="text-slate-400 shrink-0" /> {[s.street, s.city].filter(Boolean).join(", ")}</div>
                {s.phone && <div className="flex items-center gap-2 text-slate-500"><Phone size={14} className="text-slate-400 shrink-0" /> <span className="font-mono text-xs">{s.phone}</span></div>}
                {s.email && <div className="flex items-center gap-2 text-slate-500 truncate"><Mail size={14} className="text-slate-400 shrink-0" /> <span className="font-mono text-xs truncate">{s.email}</span></div>}
              </div>
              {s.website && (
                <a href={s.website} target="_blank" rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-xs text-[#2563EB] hover:underline">
                  <ExternalLink size={12} /> Sitio web
                </a>
              )}
            </div>
          ))}
          {stores.length === 0 && (
            <div className="col-span-full py-16 text-center text-slate-400 text-sm">Sin tiendas para la búsqueda</div>
          )}
        </div>
      )}

      {/* Catalogs: locations + routes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
        <div className="bg-white border border-[#E2E8F0] rounded-sm" data-testid="locations-catalog">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#E2E8F0] text-slate-700">
            <Warehouse size={15} className="text-[#2563EB]" />
            <span className="text-xs uppercase tracking-[0.05em] font-semibold">Locaciones / Almacenes</span>
            <span className="ml-auto text-xs text-slate-400 font-mono">{locations.length}</span>
          </div>
          <div className="max-h-[360px] overflow-auto thin-scroll">
            <table className="w-full text-sm">
              <tbody>
                {locations.map((l) => (
                  <tr key={l.number} className="border-b border-slate-100">
                    <td className="px-4 py-2 font-mono text-slate-900 w-14">{l.number}</td>
                    <td className="px-2 py-2 text-slate-600">{l.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-sm" data-testid="routes-catalog">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#E2E8F0] text-slate-700">
            <RouteIcon size={15} className="text-[#2563EB]" />
            <span className="text-xs uppercase tracking-[0.05em] font-semibold">Rutas de entrega</span>
            <span className="ml-auto text-xs text-slate-400 font-mono">{routes.length}</span>
          </div>
          <div className="max-h-[360px] overflow-auto thin-scroll">
            <table className="w-full text-sm">
              <tbody>
                {routes.map((r) => (
                  <tr key={r.route_code} className="border-b border-slate-100">
                    <td className="px-4 py-2 font-mono text-slate-900 w-20">{r.route_code}</td>
                    <td className="px-2 py-2 text-slate-600">{r.route_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
